import { Injectable } from '@angular/core';
import { combineLatest, map, of, shareReplay, switchMap } from 'rxjs';
import { MapFeatureManagerService } from './map-feature-manager.service';
import { isSelectableFeature, SelectableFeature } from './selectable-feature';

export interface FeatureSelectionSummary {
  readonly feature: SelectableFeature;
  readonly count: number;
  readonly objectIds: readonly (string | number)[];
}

/** Read-only projection of feature-owned selections for the composer and Agent.
 * Includes hidden features: hiding a layer preserves its selection. */
@Injectable({ providedIn: 'root' })
export class MapSelectionContextService {
  readonly selections$ = this.features.features$.pipe(
    switchMap(features => {
      const selectable = features.filter(isSelectableFeature);
      return selectable.length ? combineLatest(selectable.map(feature =>
        feature.selectionCount$.pipe(map(count => ({
          feature, count, objectIds: feature.selectedObjectIds,
        }))),
      )) : of([]);
    }),
    map(selections => selections.filter(selection => selection.count > 0)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private presenters = 0;
  /** True while a mounted page shows the selection itself, so the map need not open the Selection Workspace. */
  get presentedByPage(): boolean { return this.presenters > 0; }

  constructor(private readonly features: MapFeatureManagerService) {}

  /** Declares that the current page presents map selection; call the result when it stops. */
  presentHere(): () => void {
    this.presenters++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.presenters--;
    };
  }

  capture(): Readonly<Record<string, unknown>> {
    const snapshot: Record<string, unknown> = {};
    for (const feature of this.features.getFeatures()) {
      if (isSelectableFeature(feature) && feature.selection.capture) {
        snapshot[feature.key] = feature.selection.capture();
      }
    }
    return snapshot;
  }

  restore(snapshot: Readonly<Record<string, unknown>>): void {
    for (const feature of this.features.getFeatures()) {
      if (isSelectableFeature(feature) && Object.prototype.hasOwnProperty.call(snapshot, feature.key)) {
        feature.selection.restore?.(snapshot[feature.key]);
      }
    }
  }

  clearAll(): void {
    for (const feature of this.features.getFeatures()) {
      if (isSelectableFeature(feature)) feature.clearSelection();
    }
  }
}
